// One clock for every recency count: the patient's local calendar in their Strive time zone
// (UTC when they have none). The zone is currently fixed per patient; when time zone history
// is added, only this module should need to change.

const DAY_MS = 24 * 60 * 60 * 1000;

// How far back recency looks. The APIs return no timestamp for data older than this, so "no
// data" means none in this window, not never.
export const RECENCY_LOOKBACK_DAYS = 90;

const formatters = new Map<string, Intl.DateTimeFormat>();

export function isValidTimeZone(timeZone: string): boolean {
  if (timeZone === "") return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

// Cached by the requested zone name, so each zone is validated once.
function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    // An unknown zone would throw for every date; count those days in UTC instead.
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: isValidTimeZone(timeZone) ? timeZone : "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    });
    formatters.set(timeZone, f);
  }
  return f;
}

// The calendar date (YYYY-MM-DD) of an instant in an IANA time zone. Built from the parts
// rather than a locale's formatted string, whose layout varies between browsers.
export function localDate(instant: Date, timeZone: string): string {
  const parts = formatterFor(timeZone).formatToParts(instant);
  const part = (type: string) => parts.find((p) => p.type === type)?.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function daysBetween(from: string, to: string): number {
  return Math.round(
    (Date.parse(to + "T00:00:00Z") - Date.parse(from + "T00:00:00Z")) / DAY_MS
  );
}

export function dateDaysBefore(date: string, days: number): string {
  return new Date(Date.parse(date + "T00:00:00Z") - days * DAY_MS)
    .toISOString()
    .slice(0, 10);
}

// Calendar days, not 24-hour periods.
export function daysSince(
  timestamp: string | null,
  timeZone: string,
  now = new Date()
): number | null {
  if (timestamp === null) return null;
  return Math.max(
    0,
    daysBetween(
      localDate(new Date(timestamp), timeZone),
      localDate(now, timeZone)
    )
  );
}
